import { useMediaQuery } from 'react-responsive';
import { Navigate, Route, Routes } from 'react-router-dom';

import IndexRoute from '@/components/IndexRoute';
import InitialLoader from '@/components/InitialLoader';
import Layout from '@/components/Layout';
import CheckIfTeamExists from '@/components/Middlewares/CheckIfTeamExists';
import GuestRoute from '@/components/Middlewares/GuestRoute';
import HasPermission from '@/components/Middlewares/HasPermission';
import PrivateRoute from '@/components/Middlewares/PrivateRoute';
import MobileWarning from '@/components/MobileWarning';
import AddInvoice from '@/pages/AddInvoice';
import AllChannels from '@/pages/AllChannels';
import AmazonList from '@/pages/AmazonList';
import AmazonListSingle from '@/pages/AmazonListSingle';
import BPTestingProject from '@/pages/BPTestingProject';
import BPTestingProjects from '@/pages/BPTestingProjects';
import BankAccountsChangelog from '@/pages/BankAccountsChangelog';
import CoverImages from '@/pages/CoverImages';
import DeletedInvoicesList from '@/pages/DeletedInvoicesList';
// import EditorialDistribution from '@/pages/EditorialDistribution';
import EytvFiles from '@/pages/EytvFiles';
import FinancesChangelog from '@/pages/FinancesChangelog';
import Freelancer from '@/pages/Freelancer';
import FreelancersList from '@/pages/FreelancersList';
import GamificationBonuses from '@/pages/GamificationBonuses';
import GamificationProjects from '@/pages/GamificationProjects';
import ImageDistribution from '@/pages/ImageDistribution';
import ImageDistributionSingle from '@/pages/ImageDistributionSingle';
import ImageTool from '@/pages/ImageTool';
import ImageToolCreate from '@/pages/ImageToolCreate';
import ImageToolSettings from '@/pages/ImageToolSettings';
import ImageToolSingle from '@/pages/ImageToolSingle';
import ImagesLibrary from '@/pages/ImagesLibrary';
import InstagramImagesSerp from '@/pages/InstagramImagesSerp';
import InstagramImagesSingle from '@/pages/InstagramImagesSingle';
import InvoiceList from '@/pages/InvoiceList';
import InvoicesChangelog from '@/pages/InvoicesChangelog';
import InvoicesPreferences from '@/pages/InvoicesPreferences';
import LeaderboardOverview from '@/pages/LeaderboardOverview';
import LinksLibrary from '@/pages/LinksLibrary';
import LoginPage from '@/pages/Login';
import NotFound from '@/pages/NotFound';
import PaymentMembersChangelog from '@/pages/PaymentMembersChangelog';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import ViewGuideSettings from '@/pages/ViewGuideSettings';
import routes, { Route as RouteI } from '@/utils/routes';
import { Breakpoints } from '@/utils/theme';

import AccountNumbers from './pages/AccountNumbers';
import AmazonGoogleList from './pages/AmazonGoogleList';
import AmazonGoogleListSingle from './pages/AmazonGoogleListSingle';
import AnimatedMemes from './pages/AnimatedMemes';
import BPArticleReels from './pages/BPArticleReels';
import CommentsChecker from './pages/CommentsChecker';
import CommentsCheckerSingle from './pages/CommentsCheckerSingle';
import Companies from './pages/Companies';
import CoverImagesGenerator from './pages/CoverImagesGenerator';
import CoverImagesSingle from './pages/CoverImagesSingle';
import DQFeed from './pages/DQFeed';
import DQScheduledActions from './pages/DQScheduledActions';
import DQScheduledActionsPlaceholder from './pages/DQScheduledActions/DQScheduledActionsPlaceholder';
import Dashboard from './pages/Dashboard';
import Departments from './pages/Departments';
import EditorialDistributionQueue from './pages/EditorialDistributionQueue';
import EditorialDistributionQueuePlaceholder from './pages/EditorialDistributionQueue/EditorialDistributionQueuePlaceholder';
import EditorialDistributionSingle from './pages/EditorialDistributionSingle';
import FacebookImageStories from './pages/FacebookImageStories';
import FacebookVideoStories from './pages/FacebookVideoStories';
import FreelancerInvoices from './pages/FreelancerInvoices';
import FreelancerRoles from './pages/FreelancerRoles';
import FreelancerSubunits from './pages/FreelancerSubunits';
import FreelancersInvoicesApproval from './pages/FreelancersInvoicesApproval';
import FreelancersNotificationCenter from './pages/FreelancersNofiticationCenter';
import GenerateInvoice from './pages/GenerateInvoice';
import IgPosts from './pages/IgPosts';
import IgProfiles from './pages/IgProfiles';
import MetaBusinessAccount from './pages/MetaBusinessAccount';
import MetaBusinessAccounts from './pages/MetaBusinessAccounts';
import PublicHolidays from './pages/PublicHolidays';
import RequestVacation from './pages/RequestVacation';
import SocialPostsImporterTool from './pages/SocialPostsImporterTool';
import SocialPostsImporterResults from './pages/SocialPostsImporterTool/Results';
import Sponsorname from './pages/Sponsorname';
import UsersManagement from './pages/UsersManagement';
import VacationApprovals from './pages/VacationApprovals';
import VacationLogin from './pages/VacationLogin';
import VibeBone from './pages/Vibebone';
import VideoCompilations from './pages/VideoCompilations';
import CreateVideoCompilation from './pages/VideoCompilations/Create';
import ShowVideoCompilation from './pages/VideoCompilations/Show';
import VideoFactory from './pages/VideoFactory';
import {
  BPTestingProjectStatus,
  ImageDistributionStatus,
  VideoCompilerCompilationStatus,
} from './utils/enums';

const renderRoutes = (routes: RouteI[]) => {
  return routes.map(({ title, routes, subtitle, ...props }) => (
    <Route key={props.path} {...props}>
      {routes ? renderRoutes(routes) : null}
    </Route>
  ));
};

const App = () => {
  const isMobile = useMediaQuery({ maxWidth: Breakpoints.max.smallPhone });

  if (isMobile) return <MobileWarning />;

  return (
    <InitialLoader>
      <Routes>
        <Route path="/" element={<IndexRoute />} />
        <Route
          path="login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route
          path="vacation-login"
          element={
            <GuestRoute redirectTo="/general/freelancers/request-vacation">
              <VacationLogin />
            </GuestRoute>
          }
        />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route
          path="general"
          element={
            <PrivateRoute redirectTo="/vacation-login">
              <Layout />
            </PrivateRoute>
          }
        >
          <Route
            path="freelancers/request-vacation"
            element={<RequestVacation />}
          />
        </Route>
        <Route
          path="general"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="all-channels" element={<AllChannels />} />
          <Route path="links-library" element={<LinksLibrary />} />

          <Route path="invoices" element={<InvoiceList />} />
          <Route path="invoices/pre-received" element={<InvoiceList />} />
          <Route path="invoices/received" element={<InvoiceList />} />
          <Route path="invoices/paid" element={<InvoiceList />} />
          <Route path="invoices/all" element={<InvoiceList />} />
          <Route element={<FinancesChangelog />}>
            <Route
              path="invoices/invoices-changelog"
              element={<InvoicesChangelog />}
            />
            <Route
              path="invoices/members-changelog"
              element={<PaymentMembersChangelog />}
            />
            <Route
              path="invoices/bank-accounts-changelog"
              element={<BankAccountsChangelog />}
            />
          </Route>
          <Route path="invoices/deleted" element={<DeletedInvoicesList />} />
          <Route path="invoices/create" element={<AddInvoice />} />
          <Route
            path="invoices/receiver-list"
            element={<InvoicesPreferences />}
          />
          <Route path="freelancers/type/:type" element={<FreelancersList />} />
          <Route path="freelancers/:id" element={<Freelancer />} />
          <Route
            path="freelancers/vacation-approvals"
            element={<VacationApprovals />}
          />
          <Route path="freelancers/invoices" element={<FreelancerInvoices />} />
          <Route
            path="freelancers/invoices/generate"
            element={<GenerateInvoice />}
          />
          <Route
            path="freelancers/invoices/approvals"
            element={<FreelancersInvoicesApproval />}
          />
          <Route
            path="freelancers/public-holidays"
            element={<PublicHolidays />}
          />
          <Route
            path="freelancers-notification-center"
            element={<FreelancersNotificationCenter />}
          />
          <Route path="image-tool/type/:type" element={<ImageTool />} />

          <Route path="image-tool/create" element={<ImageToolCreate />} />

          <Route path="image-tool/:id" element={<ImageToolSingle />} />

          <Route path="image-tool/settings" element={<ImageToolSettings />} />

          <Route path="image-tool/library" element={<ImagesLibrary />} />

          <Route path="gamification">
            <Route
              path="leaderboard/:competitionType"
              element={<LeaderboardOverview />}
            />
            <Route
              path="bonuses/:competitionType"
              element={<GamificationBonuses />}
            />
            <Route path="projects/:status" element={<GamificationProjects />} />
          </Route>
          <Route path="view-guide-settings" element={<ViewGuideSettings />} />
          <Route path="eytv-files" element={<EytvFiles />} />
          <Route path="vibebone" element={<VibeBone />} />
          <Route path="vibebone/:slug" element={<VibeBone />} />

          {/* <Route path="distribution" element={<EditorialDistribution />} /> */}

          {/* <Route path="post-distribution" element={<EditorialDistribution />} /> */}
          <Route
            path="editorial-distribution-queue"
            element={<EditorialDistributionQueuePlaceholder />}
          />
          <Route
            path="editorial-distribution-queue/:pageId/:status/:network"
            element={<EditorialDistributionQueue />}
          />
          <Route
            path="dq-scheduled-actions"
            element={<DQScheduledActionsPlaceholder />}
          />
          <Route
            path="dq-scheduled-actions/:network"
            element={<DQScheduledActions />}
          />
          <Route path="dq-comments-checker" element={<CommentsChecker />} />
          <Route
            path="dq-comments-checker/:commentsCheckId"
            element={<CommentsCheckerSingle />}
          />
          {/* <Route
            path="post-distribution/status/:status"
            element={<EditorialDistribution />}
          /> */}
          <Route
            path="post-distribution/:id"
            element={<EditorialDistributionSingle />}
          />

          <Route
            path="bp-testing-projects"
            element={
              <BPTestingProjects status={BPTestingProjectStatus.ACTIVE} />
            }
          />

          <Route
            path="bp-testing-projects/ended"
            element={
              <BPTestingProjects status={BPTestingProjectStatus.ENDED} />
            }
          />

          <Route
            path="bp-testing-projects/:id"
            element={<BPTestingProject />}
          />

          <Route path="image-distribution" element={<ImageDistribution />} />
          <Route
            path="image-distribution/posted"
            element={
              <ImageDistribution status={ImageDistributionStatus.POSTED} />
            }
          />
          <Route
            path="image-distribution/scheduled"
            element={
              <ImageDistribution status={ImageDistributionStatus.SCHEDULED} />
            }
          />
          <Route
            path="image-distribution/draft"
            element={
              <ImageDistribution status={ImageDistributionStatus.DRAFT} />
            }
          />
          <Route
            path="image-distribution/:id"
            element={<ImageDistributionSingle />}
          />

          <Route
            path="cover-images/generate/:type"
            element={<CoverImagesGenerator />}
          />

          <Route
            path="video-compilations"
            element={<Navigate to="./in-process" relative="path" replace />}
          />
          <Route
            path="video-compilations/in-process"
            element={
              <VideoCompilations
                status={VideoCompilerCompilationStatus.IN_PROCESS}
              />
            }
          />
          <Route
            path="video-compilations/done"
            element={
              <VideoCompilations
                status={VideoCompilerCompilationStatus.COMPLETED}
              />
            }
          />
          <Route
            path="video-compilations/failed"
            element={
              <VideoCompilations
                status={VideoCompilerCompilationStatus.FAILED}
              />
            }
          />
          <Route
            path="video-compilations/draft"
            element={
              <VideoCompilations
                status={VideoCompilerCompilationStatus.DRAFT}
              />
            }
          />
          <Route
            path="video-compilations/:compilationId"
            element={<ShowVideoCompilation />}
          />
          <Route
            path="video-compilations/create/:type/:compilationType"
            element={<CreateVideoCompilation />}
          />

          <Route
            path="video-compilations/:compilationId/edit"
            element={<CreateVideoCompilation />}
          />

          <Route
            path="facebook-video-stories"
            element={<FacebookVideoStories />}
          />
          <Route
            path="facebook-image-stories"
            element={<FacebookImageStories />}
          />
          <Route path="dq-feed" element={<DQFeed />} />

          <Route path="video-factory" element={<VideoFactory />} />
          <Route path="video-factory/:page" element={<VideoFactory />} />

          <Route path="animated-memes" element={<AnimatedMemes />} />

          <Route path="bp-article-reels" element={<BPArticleReels />} />

          <Route path="cover-images/type/:type" element={<CoverImages />} />

          <Route path="cover-images/:id" element={<CoverImagesSingle />} />

          <Route path="amazon-products-serp" element={<AmazonList />} />

          <Route
            path="amazon-products-serp/:id"
            element={<AmazonListSingle />}
          />

          <Route path="google-products-serp" element={<AmazonGoogleList />} />

          <Route
            path="google-products-serp/:id"
            element={<AmazonGoogleListSingle />}
          />

          <Route
            path="instagram-images-serp"
            element={<InstagramImagesSerp />}
          />

          <Route
            path="instagram-images-serp/:id"
            element={<InstagramImagesSingle />}
          />

          <Route
            path="social-posts-importer-tool"
            element={<SocialPostsImporterTool />}
          />

          <Route
            path="social-posts-importer-tool/:importId"
            element={<SocialPostsImporterResults />}
          />
          <Route
            path="users-management"
            element={
              <HasPermission permissions={['manage users']} forRoute>
                <UsersManagement />
              </HasPermission>
            }
          />
          <Route
            path="meta-business-accounts"
            element={<MetaBusinessAccounts />}
          />
          <Route
            path="meta-business-accounts/:id"
            element={<MetaBusinessAccount />}
          />
          <Route path="ig-profiles" element={<IgProfiles />} />
          <Route path="ig-posts" element={<IgPosts />} />
          <Route path="sponsorname" element={<Sponsorname />} />
          <Route path="departments" element={<Departments />} />
          <Route path="account-numbers" element={<AccountNumbers />} />
          <Route path="companies" element={<Companies />} />
          <Route path="subunits" element={<FreelancerSubunits />} />
          <Route path="roles" element={<FreelancerRoles />} />
        </Route>
        <Route
          path="teams/:teamId"
          element={
            <PrivateRoute>
              <CheckIfTeamExists>
                <Layout />
              </CheckIfTeamExists>
            </PrivateRoute>
          }
        >
          <Route index element={<Dashboard />} />
          {renderRoutes(routes)}
        </Route>
        <Route path="*" element={<NotFound global />} />
      </Routes>
    </InitialLoader>
  );
};

export default App;
